import {
  InventoryItem as InventoryItemProto,
  InventoryItemJobProgressionBillingInfo,
  InventoryItemJobProgressionEssential,
  InventoryItemJobProgressionOutsourcing,
  InventoryItemJobProgressionPreparation,
  InventoryItemJobProgressionRetail,
  InventoryItemJobProgressionReview,
  InventoryItemPriceType as InventoryItemPriceTypeProto,
  InventoryItemSupportingItem,
  InventoryItemTypeSpecification as InventoryItemTypeSpecificationProto,
  ProgressionStepBillingValues,
} from '@taxfyle/api-internal/internal/inventory_item_pb'
import {
  InventoryItemDto,
  InventoryItemPriceType,
  SupportingItemBusinessInfoSpecDto,
  SupportingItemBusinessRefundMethodInfoSpecDto,
  SupportingItemDependentsInfoSpecDto,
  SupportingItemDto,
  SupportingItemNotesSpecDto,
  SupportingItemPersonalInfoSpecDto,
  SupportingItemPersonalRefundMethodInfoSpecDto,
  SupportingItemSpecDto,
  SupportingItemSpecTypesDto,
  SupportingItemSpousalInfoSpecDto,
} from './inventory-item'
import {
  decimalToNumber,
  numberToDecimal,
  timestampToISO,
} from '../../utils/grpc-util'
import { BadRequest } from 'fejl'
import { match } from 'utils/pattern-match-util'
import {
  ExtensionBillingInfoType,
  InventoryItemBookkeepingTypeSpecification,
  InventoryItemConsultationTypeSpecification,
  InventoryItemDefaultTypeSpecification,
  InventoryItemExtensionBillingType,
  InventoryItemExtensionType,
  InventoryItemExtensionTypeSpecification,
  InventoryItemTaxTypeSpecification,
  InventoryItemTypeSpecification,
} from 'admin/inventory/InventoryItem'
import {
  AllowedProgressionSteps,
  AllowedProgressionTypes,
  ExtensionBillingInfo,
  ExtensionProgressionBilling,
  ExtensionRegularBilling,
} from '../inventory/components/InventoryItemForm/ManageExtensionBillingInfo/declarations'

export function mapInventoryItemProtoToDTO(
  proto: InventoryItemProto,
): InventoryItemDto {
  const price = proto.getPrice()
  const proPrice = proto.getProPrice()

  return {
    id: proto.getId(),
    version_id: proto.getVersionId(),
    short_id: proto.getShortId(),
    name: proto.getName(),
    description: proto.getDescription(),
    workspace_id: proto.getWorkspaceId() || undefined,
    archived: proto.getArchived(),
    pro_visibility: proto.getProVisibility(),
    created_by: proto.getCreatedBy(),
    version: proto.getVersion(),
    date_created: timestampToISO(proto.getDateCreated()),
    courtesy_amount: proto.getCourtesyAmount(),
    price: price ? decimalToNumber(price) : 0,
    pro_price: proPrice ? decimalToNumber(proPrice) : 0,
    root_id: proto.getRootId() || undefined,
    price_type: mapInventoryItemPriceTypeProtoToDTO(proto.getPriceType()),
    supporting_items: proto
      .getSupportingItemsList()
      .map(mapSupportingItemProtoToDTO),
    type_specification: mapTypeSpecificationProtoToDTO(
      proto.getTypeSpecification(),
    ),
  }
}

export function mapExtensionTypeToProto(
  type: InventoryItemExtensionType,
): InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionType {
  switch (type) {
    case InventoryItemExtensionType.Personal:
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionType.PERSONAL
    case InventoryItemExtensionType.Business:
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionType.BUSINESS
    case InventoryItemExtensionType.Outsourcing:
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionType.OUTSOURCING
  }
}

export function mapExtensionBillingTypeToProto(
  type: InventoryItemExtensionBillingType,
): InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionBillingType {
  switch (type) {
    case InventoryItemExtensionBillingType.Percentage:
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionBillingType.PERCENTAGE
    case InventoryItemExtensionBillingType.FlatFee:
      return InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
        .ExtensionBillingType.FLAT_FEE
  }
}

export function mapExtensionBillingInfoToProto(
  billingInfo: ExtensionBillingInfo,
): InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo {
  switch (billingInfo.type) {
    case ExtensionBillingInfoType.Regular:
      return new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo().setRegular(
        new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.RegularBillingInfo()
          .setClientChargeAmount(
            numberToDecimal(billingInfo.clientChargeAmount),
          )
          .setProPayoutAmount(numberToDecimal(billingInfo.proPayoutAmount)),
      )
    case ExtensionBillingInfoType.Progression:
      return new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo().setJobProgression(
        mapExtensionJobProgressionBillingInfo(billingInfo),
      )
  }
}

function mapExtensionJobProgressionBillingInfo(
  billingInfo: ExtensionProgressionBilling,
): InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.JobProgressionBillingInfo {
  const billing = billingInfo.progressions.reduce((billing, progression) => {
    switch (progression.progression) {
      case AllowedProgressionTypes.OUTSOURCING:
        billing.setOutsourcing(
          progression.steps.reduce((outsourcing, step) => {
            switch (step.step) {
              case AllowedProgressionSteps.OPEN_ITEMS:
                outsourcing.setOpenItems(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.JOB_STARTED:
                outsourcing.setJobStarted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_REJECTED:
                outsourcing.setDraftRejected(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_IN_REVIEW:
                outsourcing.setDraftInReview(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_APPROVED:
                outsourcing.setDraftApproved(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return outsourcing
          }, new InventoryItemJobProgressionOutsourcing()),
        )
        break
      case AllowedProgressionTypes.REVIEW:
        billing.setReview(
          progression.steps.reduce((review, step) => {
            switch (step.step) {
              case AllowedProgressionSteps.OPEN_ITEMS:
                review.setOpenItems(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.JOB_STARTED:
                review.setJobStarted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_REJECTED:
                review.setDraftRejected(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_IN_REVIEW:
                review.setDraftInReview(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.REVIEW_COMPLETED:
                review.setReviewCompleted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_APPROVED:
                review.setDraftApproved(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return review
          }, new InventoryItemJobProgressionReview()),
        )
        break
      case AllowedProgressionTypes.RETAIL:
        billing.setRetail(
          progression.steps.reduce((retail, step) => {
            switch (step.step) {
              case AllowedProgressionSteps.OPEN_ITEMS:
                retail.setOpenItems(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.JOB_STARTED:
                retail.setJobStarted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_REJECTED:
                retail.setDraftRejected(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_IN_REVIEW:
                retail.setDraftInReview(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DRAFT_APPROVED:
                retail.setDraftApproved(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.AUTHORIZATION_SIGNED:
                retail.setAuthorizationSigned(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.RETURN_FILED:
                retail.setReturnFiled(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return retail
          }, new InventoryItemJobProgressionRetail()),
        )
        break
      case AllowedProgressionTypes.PREPARATION:
        billing.setPreparation(
          progression.steps.reduce((preparation, step) => {
            switch (step.step) {
              case AllowedProgressionSteps.OPEN_ITEMS:
                preparation.setOpenItems(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.JOB_STARTED:
                preparation.setJobStarted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.PREPARATION_COMPLETED:
                preparation.setPreparationCompleted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return preparation
          }, new InventoryItemJobProgressionPreparation()),
        )
        break
      case AllowedProgressionTypes.ESSENTIAL:
        billing.setEssential(
          progression.steps.reduce((essential, step) => {
            switch (step.step) {
              case AllowedProgressionSteps.JOB_STARTED:
                essential.setJobStarted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
              case AllowedProgressionSteps.DELIVERABLE_COMPLETED:
                essential.setDeliverableCompleted(
                  mapExtensionJobProgressionStepValue(
                    step.clientChargeAmount,
                    step.proPayoutAmount,
                  ),
                )
                break
            }
            return essential
          }, new InventoryItemJobProgressionEssential()),
        )
        break
    }

    return billing
  }, new InventoryItemJobProgressionBillingInfo())

  return new InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.JobProgressionBillingInfo().setBillingInfo(
    billing,
  )
}

function mapExtensionJobProgressionStepValue(
  clientChargeAmount: number,
  proPayoutAmount: number,
): ProgressionStepBillingValues {
  return new ProgressionStepBillingValues()
    .setClientChargeAmount(numberToDecimal(clientChargeAmount))
    .setProPayoutAmount(numberToDecimal(proPayoutAmount))
}

function mapTypeSpecificationProtoToDTO(
  spec?: InventoryItemTypeSpecificationProto,
): InventoryItemTypeSpecification {
  if (!spec) {
    throw new BadRequest('Type specification not found')
  }

  switch (spec.getSpec()?.getTypeCase()) {
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.DEFAULT:
      return new InventoryItemDefaultTypeSpecification()
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.BOOKKEEPING:
      return mapBookkeepingTypeSpecificationProtoToDTO(
        spec.getSpec()?.getBookkeeping(),
      )
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.CONSULTATION:
      return mapConsultationTypeSpecificationProtoToDTO(
        spec.getSpec()?.getConsultation(),
      )
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.TAX:
      return mapTaxTypeSpecificationProtoToDTO(spec.getSpec()?.getTax())
    case InventoryItemTypeSpecificationProto.TypeOptions.TypeCase.TAX_EXTENSION:
      return mapExtensionTypeSpecificationProtoToDTO(
        spec.getSpec()?.getTaxExtension(),
      )
    default:
      throw new BadRequest('Unsupported type specification')
  }
}

function mapConsultationTypeSpecificationProtoToDTO(
  spec?: InventoryItemTypeSpecificationProto.TypeOptions.Consultation,
): InventoryItemConsultationTypeSpecification {
  if (!spec) {
    throw new BadRequest('Consultation type specification not found')
  }

  return new InventoryItemConsultationTypeSpecification(
    spec.getLengthInMinutes(),
  )
}

function mapBookkeepingTypeSpecificationProtoToDTO(
  spec?: InventoryItemTypeSpecificationProto.TypeOptions.Bookkeeping,
): InventoryItemBookkeepingTypeSpecification {
  if (!spec) {
    throw new BadRequest('Bookkeeping type specification not found')
  }

  return new InventoryItemBookkeepingTypeSpecification(spec.getRecurring())
}

function mapTaxTypeSpecificationProtoToDTO(
  spec?: InventoryItemTypeSpecificationProto.TypeOptions.Tax,
): InventoryItemTaxTypeSpecification {
  if (
    !spec ||
    spec.getReviewPrice() === undefined ||
    spec.getReviewProPrice() === undefined
  ) {
    throw new BadRequest('Tax type specification not found')
  }

  const reviewPrice = spec.getReviewPrice()
  const reviewPayout = spec.getReviewProPrice()

  if (reviewPrice === undefined || reviewPayout === undefined) {
    throw new BadRequest('Tax type specification pricing error')
  }

  return new InventoryItemTaxTypeSpecification(
    decimalToNumber(reviewPrice),
    decimalToNumber(reviewPayout),
  )
}

function mapExtensionTypeSpecificationProtoToDTO(
  spec?: InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension,
): InventoryItemExtensionTypeSpecification {
  if (!spec || spec.getType() === undefined) {
    throw new BadRequest('Extension type specification not found')
  }

  const extensionType = spec.getType()
  const billingType = spec.getBillingType()
  const billingInfo = spec.getBillingInfo()

  if (extensionType === undefined) {
    throw new BadRequest('Extension type specification type error')
  }

  if (billingInfo === undefined) {
    throw new BadRequest('Extension type specification type error')
  }

  return new InventoryItemExtensionTypeSpecification(
    mapExtensionType(extensionType),
    mapExtensionBillingType(billingType),
    mapExtensionBillingInfo(billingInfo),
  )
}

function mapExtensionType(
  type: InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionType,
): InventoryItemExtensionType {
  switch (type) {
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionType.PERSONAL:
      return InventoryItemExtensionType.Personal
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionType.BUSINESS:
      return InventoryItemExtensionType.Business
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionType.OUTSOURCING:
      return InventoryItemExtensionType.Outsourcing
    default:
      throw new BadRequest('Unsupported extension type')
  }
}

function mapExtensionBillingType(
  type: InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension.ExtensionBillingType,
): InventoryItemExtensionBillingType {
  switch (type) {
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionBillingType.FLAT_FEE:
      return InventoryItemExtensionBillingType.FlatFee
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionBillingType.PERCENTAGE:
      return InventoryItemExtensionBillingType.Percentage
    case InventoryItemTypeSpecificationProto.TypeOptions.TaxExtension
      .ExtensionBillingType.EXTENSION_BILLING_TYPE_UNSPECIFIED:
      return InventoryItemExtensionBillingType.FlatFee
    default:
      throw new BadRequest('Unsupported extension billing type')
  }
}

function mapExtensionBillingInfo(
  billingInfo: InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo,
): ExtensionBillingInfo {
  switch (billingInfo.getTypeCase()) {
    case InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.TypeCase
      .REGULAR:
      return mapRegularBillingInfo(billingInfo.getRegular())

    case InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.TypeCase
      .JOB_PROGRESSION:
      return mapProgressionBillingInfo(billingInfo.getJobProgression())
    case InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.TypeCase
      .UNSPECIFIED:
      return mapRegularBillingInfo(billingInfo.getRegular())
    default:
      throw new BadRequest('Unsupported extension billing info')
  }
}

function mapRegularBillingInfo(
  regularBilling?: InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.RegularBillingInfo,
): ExtensionRegularBilling {
  const clientChargeAmount = regularBilling?.getClientChargeAmount()
  const proPayoutAmount = regularBilling?.getProPayoutAmount()

  return {
    type: ExtensionBillingInfoType.Regular,
    clientChargeAmount: clientChargeAmount
      ? decimalToNumber(clientChargeAmount)
      : 0,
    proPayoutAmount: proPayoutAmount ? decimalToNumber(proPayoutAmount) : 0,
  }
}

function mapProgressionBillingInfo(
  progressionBilling?: InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.JobProgressionBillingInfo,
): ExtensionProgressionBilling {
  const progressionBillingProto = progressionBilling
    ?.getBillingInfo()
    ?.toObject()

  const progressions = []

  if (progressionBillingProto) {
    for (const [progressionKey, progressionValue] of Object.entries(
      progressionBillingProto,
    )) {
      const steps = []

      for (const [stepKey, stepValue] of Object.entries(progressionValue)) {
        const clientChargeAmount = stepValue.clientChargeAmount
          ? decimalToNumber(stepValue.clientChargeAmount)
          : 0

        const proPayoutAmount = stepValue.proPayoutAmount
          ? decimalToNumber(stepValue.proPayoutAmount)
          : 0

        if (clientChargeAmount || proPayoutAmount) {
          steps.push({
            step: mapProgressionStepNameProtoToEnum(stepKey),
            clientChargeAmount,
            proPayoutAmount,
          })
        }
      }

      if (steps.length) {
        progressions.push({
          progression: mapProgressionNameProtoToEnum(progressionKey),
          steps,
        })
      }
    }
  }

  return {
    type: ExtensionBillingInfoType.Progression,
    progressions,
  }
}

function mapProgressionNameProtoToEnum(progression: string) {
  switch (progression) {
    case 'outsourcing':
      return AllowedProgressionTypes.OUTSOURCING
    case 'essential':
      return AllowedProgressionTypes.ESSENTIAL
    case 'retail':
      return AllowedProgressionTypes.RETAIL
    case 'preparation':
      return AllowedProgressionTypes.PREPARATION
    case 'review':
      return AllowedProgressionTypes.REVIEW
    default:
      throw new BadRequest('Unsupported progression')
  }
}

function mapProgressionStepNameProtoToEnum(step: string) {
  switch (step) {
    case 'jobStarted':
      return AllowedProgressionSteps.JOB_STARTED
    case 'openItems':
      return AllowedProgressionSteps.OPEN_ITEMS
    case 'draftInReview':
      return AllowedProgressionSteps.DRAFT_IN_REVIEW
    case 'draftRejected':
      return AllowedProgressionSteps.DRAFT_REJECTED
    case 'draftApproved':
      return AllowedProgressionSteps.DRAFT_APPROVED
    case 'authorizationSigned':
      return AllowedProgressionSteps.AUTHORIZATION_SIGNED
    case 'reviewCompleted':
      return AllowedProgressionSteps.REVIEW_COMPLETED
    case 'preparationCompleted':
      return AllowedProgressionSteps.PREPARATION_COMPLETED
    case 'returnFiled':
      return AllowedProgressionSteps.RETURN_FILED
    case 'deliverableCompleted':
      return AllowedProgressionSteps.DELIVERABLE_COMPLETED
    default:
      throw new BadRequest('Unsupported progression step')
  }
}

function mapInventoryItemPriceTypeProtoToDTO(
  priceType: InventoryItemPriceTypeProto,
): InventoryItemPriceType {
  return match(priceType, {
    [InventoryItemPriceTypeProto.FEE]: () => InventoryItemPriceType.FEE,
    [InventoryItemPriceTypeProto.FLAT]: () => InventoryItemPriceType.FLAT,
    [InventoryItemPriceTypeProto.MARKUP]: () => InventoryItemPriceType.MARKUP,
    [InventoryItemPriceTypeProto.MARKDOWN]: () =>
      InventoryItemPriceType.MARKDOWN,
  })
}

function mapSupportingItemProtoToDTO(
  supportingItemProto: InventoryItemSupportingItem,
): SupportingItemDto {
  return {
    required: supportingItemProto.getRequired(),
    spec: mapSupportingItemSpecProtoToDTO(supportingItemProto.getSpec()),
  }
}

function mapSupportingItemSpecProtoToDTO(
  specProto?: InventoryItemSupportingItem.Spec,
): SupportingItemSpecDto {
  if (!specProto) {
    throw new BadRequest('No supporting item spec type found')
  }

  switch (specProto.getTypeCase()) {
    case InventoryItemSupportingItem.Spec.TypeCase.DOCUMENT:
      return mapSupportingItemDocumentSpecProtoToDTO(specProto.getDocument())
    case InventoryItemSupportingItem.Spec.TypeCase.BUSINESS_INFO:
      return {
        type: SupportingItemSpecTypesDto.BUSINESS_INFO,
      } as SupportingItemBusinessInfoSpecDto
    case InventoryItemSupportingItem.Spec.TypeCase.BUSINESS_REFUND_METHOD:
      return {
        type: SupportingItemSpecTypesDto.BUSINESS_REFUND_METHOD_INFO,
      } as SupportingItemBusinessRefundMethodInfoSpecDto
    case InventoryItemSupportingItem.Spec.TypeCase.DEPENDENTS_INFO:
      return {
        type: SupportingItemSpecTypesDto.DEPENDENTS_INFO,
      } as SupportingItemDependentsInfoSpecDto
    case InventoryItemSupportingItem.Spec.TypeCase.NOTES:
      return {
        type: SupportingItemSpecTypesDto.NOTES,
      } as SupportingItemNotesSpecDto
    case InventoryItemSupportingItem.Spec.TypeCase.PERSONAL_INFO:
      return {
        type: SupportingItemSpecTypesDto.PERSONAL_INFO,
      } as SupportingItemPersonalInfoSpecDto
    case InventoryItemSupportingItem.Spec.TypeCase.PERSONAL_REFUND_METHOD:
      return {
        type: SupportingItemSpecTypesDto.PERSONAL_REFUND_METHOD_INFO,
      } as SupportingItemPersonalRefundMethodInfoSpecDto
    case InventoryItemSupportingItem.Spec.TypeCase.SPOUSAL_INFO:
      return {
        type: SupportingItemSpecTypesDto.SPOUSAL_INFO,
      } as SupportingItemSpousalInfoSpecDto

    default:
      throw new BadRequest('Unsupported supporting item spec type')
  }
}

function mapSupportingItemDocumentSpecProtoToDTO(
  documentSpecProto?: InventoryItemSupportingItem.Spec.Document,
): SupportingItemSpecDto {
  if (!documentSpecProto) {
    throw new BadRequest('Supporting item spec document not found')
  }

  return {
    type: SupportingItemSpecTypesDto.DOCUMENT,
    document: {
      type_short_id: documentSpecProto.getTypeShortId(),
    },
  }
}
